import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

/**
 * Wie viel Raum der Projekt-Canvas gerade beansprucht. Eigener Typ statt
 * `CanvasPanelState` aus `features/taskManagement`: `contexts/` liegt in der
 * Abhängigkeitskette VOR den Features und darf nicht aus ihnen importieren.
 */
export type CanvasMode = 'none' | 'closing' | 'peek' | 'split' | 'maximized'

/**
 * Die beiden Größen, die die Fläche BEANSPRUCHEN — die Navigation weicht.
 *
 * `peek` und `closing` fehlen hier mit Absicht. Beide verlangen, dass die
 * Breadcrumb ihnen weicht wie dem offenen Canvas, aber nicht, dass die
 * Navigation zusammenfährt:
 *
 * - Der **Canvas-Anriss** (`peek`, der 77px-Streifen bei geschlossenem Canvas
 *   mit Elementen) ist eine Darstellung von „geschlossen", keine Arbeitsfläche.
 * - `closing` ist die Zwischenphase des Zuziehens: Der Canvas hält dabei seine
 *   Höhe (die Zeile bleibt draußen), gibt die Fläche aber schon frei — die
 *   Navigation fährt GEMEINSAM mit ihm herein, nicht danach.
 */
const FOCUSED_MODES: readonly CanvasMode[] = ['split', 'maximized']

interface CanvasFocusContextValue {
  /** Wie weit der Canvas DIESES Tabs offen ist. `none`, wenn er nichts gemeldet hat. */
  modeFor: (tabId: string | null) => CanvasMode
  /** Ein Panel meldet, wie weit SEIN Canvas offen ist. */
  setCanvasMode: (tabId: string, mode: CanvasMode) => void
  /** Ein Panel verschwindet (Canvas zu, Tab geschlossen) und nimmt seine Meldung zurück. */
  clearCanvasMode: (tabId: string) => void
  /** Ob die **Aufgaben-Details** dieses Tabs die rechte Hälfte beanspruchen. */
  detailsOpenFor: (tabId: string | null) => boolean
  /**
   * Was die Liste dieses Tabs neben offenen Details mindestens braucht — `undefined`, wenn die
   * Seite nichts gemeldet hat (dann gilt die Vorgabe, `navigationVisibility.ts`) oder zu ist.
   */
  detailsListMinWidthFor: (tabId: string | null) => number | undefined
  /**
   * Eine Seite meldet, ob ihre Aufgaben-Details offen stehen — und, wenn ihre Liste mehr Platz
   * braucht als eine Aufgabenliste, wie viel (Demnächst, #226).
   */
  setDetailsOpen: (tabId: string, open: boolean, listMinWidth?: number) => void
  /** Die Seite verschwindet und nimmt ihre Meldung zurück. */
  clearDetailsOpen: (tabId: string) => void
  /**
   * Wie schmal die Liste dieses Tabs werden darf, wenn sie mehr braucht als eine Aufgabenliste — auch ohne offene
   * Details (Demnächst, #296). `undefined`: nichts gemeldet, es gilt die Vorgabe (`navigationVisibility.ts`).
   */
  pageListMinWidthFor: (tabId: string | null) => number | undefined
  /** Eine Seite meldet, was ihre Liste immer braucht. */
  setPageListMinWidth: (tabId: string, width: number) => void
  /** Die Seite verschwindet und nimmt ihre Meldung zurück. */
  clearPageListMinWidth: (tabId: string) => void
  /**
   * Um wie viel Pixel die Zeile neben der Pinnwand wächst, wenn die Pinnwand JETZT aufgeht — weil die Navigation
   * dabei weicht (#297); negativ, wenn sie zurückkommt (die Details gehen mit zu). 0, wenn sie bleibt, wie sie ist.
   */
  forecastRowGrowth: () => number
  /** `MainLayout` hinterlegt die Rechnung — nur es kennt alles, was das Weichen entscheidet. */
  setRowGrowthForecast: (forecast: () => number) => void
}

const CanvasFocusContext = createContext<CanvasFocusContextValue>({
  modeFor: () => 'none',
  setCanvasMode: () => {},
  clearCanvasMode: () => {},
  detailsOpenFor: () => false,
  detailsListMinWidthFor: () => undefined,
  setDetailsOpen: () => {},
  clearDetailsOpen: () => {},
  pageListMinWidthFor: () => undefined,
  setPageListMinWidth: () => {},
  clearPageListMinWidth: () => {},
  forecastRowGrowth: () => 0,
  setRowGrowthForecast: () => {},
})

/**
 * Lets the Projekt-Canvas tell `MainLayout` that it wants the room — the left
 * navigation slides out while a canvas is open — und der `ProjectPage`, ob es
 * bei der halben Fläche bleibt oder die Aufgabenliste ganz weichen soll.
 *
 * A context rather than props: the value would otherwise have to travel
 * `TabStack → TabContent → ProjectPage → ProjectCanvasPanel`, through two
 * memoized components that were moved onto context for exactly this reason
 * (see the `ActiveTabContext` note in `TabContent.tsx`).
 *
 * **Der Modus liegt PRO TAB, nicht als ein Wert für die App.**
 *
 * Als ein Wert schlug der Zustand eines Tabs auf die Seite eines anderen durch.
 * Gemeldet am 07.09.2026: Tab A maximiert, Tab B geteilt — beim Wechsel zu B
 * schob sich dessen Aufgabenliste von links herein. In der Bildschirmaufnahme
 * stand ihr Inhalt für ein paar Frames ganz links außerhalb; das ist
 * `translateX(-100%)`, die Regel für `--canvas-maximized` (`ProjectPage.scss`).
 * Die Seite von B trug also kurz den maximierten Zustand von A, und die
 * Dauer-Transition auf der Spalte animierte den Sprung zurück.
 *
 * Nach Tab-ID abgelegt gibt es das nicht mehr: Die Meldung eines Tabs bleibt
 * liegen, während ein anderer vorn ist, und ein inaktives Panel überschreibt
 * dem aktiven nichts — was früher die `isActiveTab`-Sperre im Panel leisten
 * musste, und was ihr nie ganz gelang.
 *
 * **Wer fragt, nennt seinen Tab.** Der Provider selbst kennt den aktiven Tab
 * nicht: Er sitzt über `MainLayout`, der `TabManagerProvider` aber darin — von
 * hier aus ist der aktive Tab nicht zu sehen. `MainLayout` fragt deshalb nach
 * dem aktiven Tab, eine Seite im Slot nach ihrem eigenen (`useTabId()`).
 *
 * The default is a no-op so pages rendered outside `MainLayout` — the
 * standalone preferences window, Storybook — mount without a provider.
 */
export const CanvasFocusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modeByTab, setModeByTab] = useState<Record<string, CanvasMode>>({})
  /*
    **Die Aufgaben-Details liegen hier, obwohl sie kein Canvas sind.**

    Weil sie dieselbe Frage beantworten: Beansprucht rechts etwas die halbe
    Fläche? Wer mit Ja antwortet, schickt die **Breadcrumb** aus dem Fluss —
    sonst begänne die Karte unter ihr statt an der Oberkante der Inhaltskarte
    (`MainLayout.scss`). Ein zweiter Kontext für dieselbe Frage hätte zwei
    Quellen für eine Zeile ergeben.

    Pro Tab, aus demselben Grund wie der Modus darüber.
  */
  // Offen = Eintrag vorhanden; der Wert ist der gemeldete Platzbedarf der Liste (oder `null`).
  const [detailsByTab, setDetailsByTab] = useState<Record<string, number | null>>({})
  // Was die Liste einer Seite immer braucht (Demnächst) — Fenster und Navigation richten sich danach (#296).
  const [pageListMinByTab, setPageListMinByTab] = useState<Record<string, number>>({})
  /*
    Im Ref, nicht im State: Gefragt wird nur im Moment, in dem die Pinnwand ihre Bewegung startet (#297). Als Wert
    im State ließe jede Breitenänderung des Fensters die ganze App-Hülle neu rendern.
  */
  const rowGrowthForecastRef = useRef<() => number>(() => 0)
  const forecastRowGrowth = useCallback(() => rowGrowthForecastRef.current(), [])
  const setRowGrowthForecast = useCallback((forecast: () => number) => {
    rowGrowthForecastRef.current = forecast
  }, [])

  const setPageListMinWidth = useCallback((tabId: string, width: number) => {
    setPageListMinByTab(prev => (prev[tabId] === width ? prev : { ...prev, [tabId]: width }))
  }, [])

  const clearPageListMinWidth = useCallback((tabId: string) => {
    setPageListMinByTab(prev => {
      if (!(tabId in prev)) return prev
      const next = { ...prev }
      delete next[tabId]
      return next
    })
  }, [])

  const setCanvasMode = useCallback((tabId: string, mode: CanvasMode) => {
    setModeByTab(prev => (prev[tabId] === mode ? prev : { ...prev, [tabId]: mode }))
  }, [])

  const clearCanvasMode = useCallback((tabId: string) => {
    setModeByTab(prev => {
      if (!(tabId in prev)) return prev
      const next = { ...prev }
      delete next[tabId]
      return next
    })
  }, [])

  const setDetailsOpen = useCallback((tabId: string, open: boolean, listMinWidth?: number) => {
    setDetailsByTab(prev => {
      if (!open) {
        if (!(tabId in prev)) return prev
        const next = { ...prev }
        delete next[tabId]
        return next
      }
      const width = listMinWidth ?? null
      return tabId in prev && prev[tabId] === width ? prev : { ...prev, [tabId]: width }
    })
  }, [])

  const clearDetailsOpen = useCallback((tabId: string) => {
    setDetailsByTab(prev => {
      if (!(tabId in prev)) return prev
      const next = { ...prev }
      delete next[tabId]
      return next
    })
  }, [])

  const value = useMemo(() => ({
    modeFor: (tabId: string | null) => (tabId && modeByTab[tabId]) || 'none',
    setCanvasMode,
    clearCanvasMode,
    detailsOpenFor: (tabId: string | null) => !!tabId && tabId in detailsByTab,
    detailsListMinWidthFor: (tabId: string | null) => (tabId ? detailsByTab[tabId] ?? undefined : undefined),
    setDetailsOpen,
    clearDetailsOpen,
    pageListMinWidthFor: (tabId: string | null) => (tabId ? pageListMinByTab[tabId] : undefined),
    setPageListMinWidth,
    clearPageListMinWidth,
    forecastRowGrowth,
    setRowGrowthForecast,
  }), [modeByTab, setCanvasMode, clearCanvasMode, detailsByTab, setDetailsOpen, clearDetailsOpen, pageListMinByTab, setPageListMinWidth, clearPageListMinWidth, forecastRowGrowth, setRowGrowthForecast])

  return <CanvasFocusContext.Provider value={value}>{children}</CanvasFocusContext.Provider>
}

export const useCanvasFocus = (): CanvasFocusContextValue => useContext(CanvasFocusContext)

/** Ob die Aufgaben-Details EINES Tabs offen stehen — für `MainLayout` die des aktiven. */
export function useDetailsPanelOpen(tabId: string | null): boolean {
  return useCanvasFocus().detailsOpenFor(tabId)
}

/** Was die Liste EINES Tabs neben offenen Details mindestens braucht (#226); sonst `undefined`. */
export function useDetailsListMinWidth(tabId: string | null): number | undefined {
  return useCanvasFocus().detailsListMinWidthFor(tabId)
}

/** Was die Liste EINES Tabs immer mindestens braucht (#296); sonst `undefined`. */
export function usePageListMinWidth(tabId: string | null): number | undefined {
  return useCanvasFocus().pageListMinWidthFor(tabId)
}

/** Der Canvas-Zustand EINES Tabs — für `MainLayout` der aktive, für eine Seite ihr eigener. */
export function useCanvasMode(tabId: string | null): {
  canvasMode: CanvasMode
  isCanvasFocused: boolean
} {
  const { modeFor } = useCanvasFocus()
  const canvasMode = modeFor(tabId)
  return { canvasMode, isCanvasFocused: FOCUSED_MODES.includes(canvasMode) }
}
